import { Key, ReactNode } from 'react';
import { MdDone } from 'react-icons/md';
import { AircraftIcon } from '@icons';
import { Tooltip } from '@ds';
import { ChipWithStatus, TableColumnType } from '@ui';
import { AircraftMarkerListItem, AircraftStatusEnum, AirportListItemType } from '@lp/features/RoutePlanner/types';
import { AirportType } from '@/api/apis/facilities';
import { ResourceLogisticsStatus } from '@/api/apis/case';
import { convertMsToTimeString, getFormattedTimeRange } from '@/utils/helpers';
import { GroundVehicleType } from '@/api/apis/route';
import { MINUTE_IN_MS } from '@/constants/date.constants';

const tenMinutes = 10 * MINUTE_IN_MS;

export const STATUS_TEXT_MAP: Record<AircraftStatusEnum, string> = {
  [AircraftStatusEnum.onDuty]: 'On Duty',
  [AircraftStatusEnum.reserved]: 'Reserved',
  [AircraftStatusEnum.onRest]: 'On Rest',
  [AircraftStatusEnum.unavailable]: 'N/A',
};

export const STATUS_MAP: Record<AircraftStatusEnum, ResourceLogisticsStatus> = {
  [AircraftStatusEnum.onDuty]: ResourceLogisticsStatus.Available,
  [AircraftStatusEnum.reserved]: ResourceLogisticsStatus.Reserved,
  [AircraftStatusEnum.onRest]: ResourceLogisticsStatus.OnRest,
  [AircraftStatusEnum.unavailable]: ResourceLogisticsStatus.Reserved,
};

export const airportsMapper = (item: AirportType): AirportListItemType => ({
  value: `${item.id}`,
  maoId: `${item.maoId}`,
  name: item.iata || item.icao || item.name,
  location: item.location,
  fbos: item.fbos,
});

export const AIRCRAFT_TABLE_COLUMNS: TableColumnType[] = [
  {
    key: 'registration',
    label: 'Registration',
    isSortable: false,
  },
  {
    key: 'status',
    label: 'Status',
    isSortable: false,
  },
  {
    key: 'availability',
    label: 'Availability',
    isSortable: false,
  },
  {
    key: 'currentAirport',
    label: 'Current Airport',
    isSortable: false,
  },
  {
    key: 'isSelected',
    label: '',
    isSortable: false,
    minWidthClassName: 'w-9',
  },
];

export const getRenderAircraftTableCell =
  (selectedAircraft: AircraftMarkerListItem | null) =>
  (aircraft: AircraftMarkerListItem, columnKey: Key): ReactNode => {
    const { id, registration, reservations, availabilities, flightTimeMs, status } = aircraft;
    const reservationDonorId = reservations[0]?.caseName;
    const currentAvailability = availabilities[0];

    const flightTime = flightTimeMs && flightTimeMs >= tenMinutes ? convertMsToTimeString(flightTimeMs) : null;
    const currentAirportCode = currentAvailability
      ? currentAvailability.airport.iata || currentAvailability.airport.icao
      : null;

    const currentAirportDisplay = currentAirportCode
      ? `${currentAirportCode}${flightTime ? ` + REP ${flightTime}` : ''}`
      : '-';

    switch (columnKey) {
      case 'registration':
        return (
          <div className="flex flex-row items-center gap-3">
            <AircraftIcon height={16} width={16} />
            <p className="text-m-m text-base-5">{registration}</p>
          </div>
        );
      case 'status':
        return (
          <Tooltip isDisabled={!reservationDonorId} content={reservationDonorId}>
            <div>
              <ChipWithStatus variant="dot" size="xxs" color="secondary" status={STATUS_MAP[status]}>
                {STATUS_TEXT_MAP[status]}
              </ChipWithStatus>
            </div>
          </Tooltip>
        );
      case 'availability':
        return (
          <p className="text-m-r text-base-5">
            {currentAvailability
              ? getFormattedTimeRange(
                  currentAvailability.startUtc,
                  currentAvailability.endUtc,
                  currentAvailability.airport.ianaTimeZone,
                )
              : '--:--'}
          </p>
        );
      case 'currentAirport':
        return <span className="text-m-r text-base-5">{currentAirportDisplay}</span>;
      case 'isSelected':
        return (
          <div className="w-9 flex justify-center items-center">
            {selectedAircraft?.id === id && <MdDone color="#000" size={12} />}
          </div>
        );
      default:
        return null;
    }
  };

export const VEHICLE_TYPES_SELECT_LIST = [
  {
    value: GroundVehicleType.Sedan,
    name: GroundVehicleType.Sedan,
  },
  {
    value: GroundVehicleType.SUV,
    name: GroundVehicleType.SUV,
  },
];
